"use client";

import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { Avatar, AvatarFallback, AvatarImage } from "@kit/ui/avatar";
import { Button } from "@kit/ui/button";

interface VideoInfoProps {
    title: string;
    views?: string;
    uploadDate?: string;
    channelName: string;
    channelAvatar: string;
    subscribers?: string;
    onDataChange: (key: string, value: string) => void;
    isEditable?: boolean;
    hideSubscribe?: boolean;
}

export default function VideoInfo({
    title,
    views,
    uploadDate,
    channelName,
    channelAvatar,
    subscribers,
    onDataChange,
    isEditable,
    hideSubscribe,
}: VideoInfoProps) {
    const [avatarSrc, setAvatarSrc] = useState(channelAvatar);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        const newAvatarUrl = URL.createObjectURL(file);
        setAvatarSrc(newAvatarUrl);
        onDataChange("channelAvatar", newAvatarUrl);
    }, [onDataChange]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "image/*": [] },
        multiple: false,
    });

    return (
        <div className="p-4">
            <h1
                className="text-2xl font-bold mb-2 outline-none"
                contentEditable={isEditable}
                onBlur={(e) => onDataChange("title", e.currentTarget.textContent || "")}
                suppressContentEditableWarning
            >
                {title}
            </h1>
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <div {...getRootProps()} className="relative cursor-pointer mr-3">
                        <Avatar className="h-10 w-10">
                            <AvatarImage src={avatarSrc} alt={channelName} />
                            <AvatarFallback>{channelName.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <input {...getInputProps()} />
                        {isDragActive && (
                            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                                <p className="text-white text-xs text-center">Drop</p>
                            </div>
                        )}
                    </div>
                    <div>
                        <h2
                            className="font-bold outline-none text-lg"
                            contentEditable={isEditable}
                            onBlur={(e) =>
                                onDataChange("channelName", e.currentTarget.textContent || "")
                            }
                            suppressContentEditableWarning
                        >
                            {channelName}
                        </h2>
                        {subscribers && (
                            <p
                                className="text-sm text-gray-500 outline-none"
                                contentEditable={isEditable}
                                onBlur={(e) =>
                                    onDataChange("subscribers", e.currentTarget.textContent || "")
                                }
                                suppressContentEditableWarning
                            >
                                Subscribers: {subscribers}
                            </p>
                        )}
                    </div>
                </div>
                {!hideSubscribe && (
                    <Button variant="destructive" className="bg-red-600 hover:bg-red-500" disabled>
                        Subscribe
                    </Button>
                )}
            </div>
            <div className="mt-4 text-sm text-gray-500">
                {views && (
                    <>
                        <span
                            className="outline-none mr-2"
                            contentEditable={isEditable}
                            onBlur={(e) => onDataChange("views", e.currentTarget.textContent || "")}
                            suppressContentEditableWarning
                        >
                            Views: {views}
                        </span>
                        •
                    </>
                )}
                {uploadDate && (
                    <span
                        className="outline-none ml-2"
                        contentEditable={isEditable}
                        onBlur={(e) =>
                            onDataChange("uploadDate", e.currentTarget.textContent || "")
                        }
                        suppressContentEditableWarning
                    >
                        Published: {uploadDate}
                    </span>
                )}
            </div>
        </div>
    );
}
